import { useState } from "react";
import { Send, CheckCircle2, Mail, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      trackEvent("contact_submitted");
      toast.success("Message sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative bg-[var(--tmg-ivory)] border-b border-[var(--tmg-line)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pt-16 pb-16 sm:pt-24 sm:pb-20">
          <p className="tmg-overline mb-6 tmg-fade-up">— Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight max-w-4xl tmg-fade-up-d1">
            Get in touch.
          </h1>
          <p className="mt-8 text-base sm:text-lg text-[var(--tmg-muted)] max-w-2xl leading-relaxed tmg-fade-up-d2">
            Have a question about AI training, workflows, app building, business consulting,
            or speaking? Send a message and we will respond soon.
          </p>
        </div>
      </section>

      <section className="tmg-section">
        <div className="tmg-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="tmg-overline-gold mb-5">— Reach Out Directly</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-8">
              We respond within 1–2 business days.
            </h2>
            <ul className="space-y-5 text-sm">
              <li className="flex items-center gap-4">
                <span className="w-10 h-10 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </span>
                <a href="mailto:info@tmariegreene.com" className="tmg-link-underline">info@tmariegreene.com</a>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-10 h-10 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] flex items-center justify-center">
                  <Phone className="w-4 h-4" />
                </span>
                <a href="tel:+13137431904" data-testid="contact-phone-link" className="tmg-link-underline">(313) 743-1904</a>
              </li>
            </ul>
            <div className="mt-12 p-8 bg-[var(--tmg-ivory-2)] border-l-2 border-[var(--tmg-gold)]">
              <p className="tmg-overline mb-3">— Quick Tip</p>
              <p className="text-sm text-[var(--tmg-obsidian)] leading-relaxed">
                Ready to talk strategy? Skip the form and{" "}
                <a href="/book-call" className="underline decoration-[var(--tmg-gold)] underline-offset-4">book a discovery call</a>{" "}
                directly on our calendar.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="bg-white border border-[var(--tmg-gold)] p-10 sm:p-14 text-center">
                <CheckCircle2 className="w-14 h-14 text-[var(--tmg-gold)] mx-auto mb-6" />
                <h3 className="font-display text-3xl font-semibold mb-3">Message sent.</h3>
                <p className="text-[var(--tmg-muted)] max-w-md mx-auto">
                  Thank you — your message has been received and will be reviewed shortly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white border border-[var(--tmg-line)] p-8 sm:p-10 space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="tmg-overline">Name *</Label>
                    <Input data-testid="contact-name" required value={form.name} onChange={handle("name")} />
                  </div>
                  <div className="space-y-2">
                    <Label className="tmg-overline">Email *</Label>
                    <Input data-testid="contact-email" type="email" required value={form.email} onChange={handle("email")} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="tmg-overline">Phone</Label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={handle("phone")} />
                </div>
                <div className="space-y-2">
                  <Label className="tmg-overline">Message *</Label>
                  <Textarea data-testid="contact-message" rows={6} required value={form.message} onChange={handle("message")} />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="tmg-btn-primary w-full sm:w-auto disabled:opacity-60"
                >
                  {submitting ? "Sending…" : (<>Send Message <Send className="w-4 h-4" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
